import { CdkOverlayOrigin, ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Component, contentChildren, forwardRef, input, TemplateRef, viewChild, ViewContainerRef, ViewEncapsulation, inject, OnDestroy } from '@angular/core';
import { ContentDropdownSubmenuCabinetComponent } from './content-dropdown-submenu.component';
import { MENU_HOST, MenuHost, isActionClick, menuItems, moveMenuFocus } from './menu-keyboard';

/**
 * A menu opened from a trigger: a boxed `select` trigger with a title, or an
 * `icon` kebab for row actions. The menu follows the ARIA menu pattern: arrow
 * keys and Home/End move between items, Escape or Tab closes it and returns
 * focus to the trigger. Picking an item returns focus to the trigger before
 * the item's action runs, so a dialog it opens hands focus back there.
 */
@Component({
    selector: 'app-content-dropdown-cabinet',
    templateUrl: './content-dropdown.component.html',
    styleUrls: ['./content-dropdown.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [CdkOverlayOrigin],
    providers: [{ provide: MENU_HOST, useExisting: forwardRef(() => ContentDropdownCabinetComponent) }]
})
export class ContentDropdownCabinetComponent implements OnDestroy, MenuHost {
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);

  trigger = viewChild.required(CdkOverlayOrigin);
  dropdownTemplate = viewChild.required<TemplateRef<unknown>>('dropdownTemplate');
  submenus = contentChildren(ContentDropdownSubmenuCabinetComponent);
  overlayRef!: OverlayRef;
  isOpen = false;

  title = input<string | undefined>(undefined);
  size = input<'xs' | 'sm' | 'lg' | undefined>(undefined);
  /**
   * `select`: a boxed trigger with the title; `icon`: a borderless kebab for
   * row actions (needs `ariaLabel`); `chip`: a pill like the filter chips,
   * with a plus sign ("Add filter").
   */
  variant = input<'select' | 'icon' | 'chip'>('select');
  /** Accessible name for a trigger that shows only an icon. */
  ariaLabel = input<string | null>(null);

  positions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetX: 0,
      offsetY: 4
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetX: 0,
      offsetY: -4
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetX: 0,
      offsetY: 4
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetX: 0,
      offsetY: -4
    }
  ];

  private closeDropdownBind = (): void => this.closeDropdown();
  private restoreBeforeAction = (event: Event): void => {
    if (isActionClick(event)) this.restoreTriggerFocus();
  };

  toggleDropdown(): void {
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown(focus: 'panel' | 'first' | 'last' = 'panel'): void {
    if (!this.overlayRef) {
      const positionStrategy = this.overlay.position()
        .flexibleConnectedTo(this.trigger().elementRef)
        // A chip sits at the start of a row, so its menu opens towards the end.
        .withPositions(this.variant() === 'chip' ? [...this.positions.slice(2), ...this.positions.slice(0, 2)] : this.positions)
        .withFlexibleDimensions(false)
        .withPush(true)
        .withViewportMargin(10);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop'
      });

      this.overlayRef.backdropClick().subscribe(() => this.closeDropdown(false));
      // Capture: runs before the item's own click handlers.
      this.overlayRef.overlayElement.addEventListener('click', this.restoreBeforeAction, true);
      this.overlayRef.overlayElement.addEventListener('click', this.closeDropdownBind);
      this.overlayRef.detachments().subscribe(() => this.isOpen = false);
    }

    const templatePortal = new TemplatePortal(this.dropdownTemplate(), this.viewContainerRef);
    this.overlayRef.attach(templatePortal);
    this.isOpen = true;
    this.focusInMenu(focus);
  }

  /** Moves focus to the trigger without closing (an item is about to run its action). */
  restoreTriggerFocus(): void {
    this.triggerElement()?.focus({ preventScroll: true });
  }

  /** Closes the menu; focus goes back to the trigger when it was inside the menu. */
  closeDropdown(restoreFocus = this.menuHasFocus()): void {
    this.submenus().forEach(s => s.close());
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
    this.isOpen = false;
    if (restoreFocus) {
      this.triggerElement()?.focus();
    }
  }

  private triggerElement(): HTMLElement | null {
    return (this.trigger().elementRef.nativeElement as HTMLElement | null) ?? null;
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.isOpen) this.closeDropdown(true);
        else this.openDropdown('first');
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen) this.openDropdown('first');
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen) this.openDropdown('last');
        break;
    }
  }

  onPanelKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' || event.key === 'Tab') {
      event.preventDefault();
      this.closeDropdown(true);
      return;
    }
    if (moveMenuFocus(event.currentTarget as HTMLElement, event.key)) {
      event.preventDefault();
    }
  }

  private panel(): HTMLElement | null {
    return this.overlayRef?.overlayElement.querySelector<HTMLElement>('[role="menu"]') ?? null;
  }

  /** Focus is in this menu or one of its submenus (not in a dialog an item opened). */
  private menuHasFocus(): boolean {
    if (!this.isOpen) return false;
    const active = document.activeElement;
    if (!active) return false;
    return !!this.overlayRef?.overlayElement.contains(active) || this.submenus().some((s) => s.containsFocus());
  }

  private focusInMenu(focus: 'panel' | 'first' | 'last'): void {
    const panel = this.panel();
    if (!panel) return;
    const items = menuItems(panel);
    const target = focus === 'first' ? items[0] : focus === 'last' ? items[items.length - 1] : undefined;
    (target ?? panel).focus({ preventScroll: true });
  }

  ngOnDestroy(): void {
    // detach() leaves the overlay alive; only dispose() releases its host
    // element and the subscriptions taken in openDropdown. These sit on table
    // rows, so every pagination or filter change would otherwise strand one
    // per row that had ever been opened.
    if (this.overlayRef) {
      this.overlayRef.overlayElement?.removeEventListener('click', this.closeDropdownBind);
      this.overlayRef.overlayElement?.removeEventListener('click', this.restoreBeforeAction, true);
      this.overlayRef.dispose();
    }
  }
}
